import jsPDF from 'jspdf';
import {
  SCHOOL_CONSENT_TITLE,
  DEFAULT_SCHOOL_NAME,
  HEADER_LABELS,
  withSchool,
  bulletLine,
} from '../data/schoolConsentText';

const BRAND_BLUE = [29, 44, 95];
const PAGE_W = 210;
const MARGIN = 14;
const CONTENT_W = PAGE_W - MARGIN * 2;
const LINE_H = 6.5;

// ── header ────────────────────────────────────────────────────────────────────
function addHeader(doc, noteType, clientName, date) {
  doc.setFillColor(...BRAND_BLUE);
  doc.rect(0, 0, PAGE_W, 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Avenues Unlimited Counseling Center', PAGE_W / 2, 14, { align: 'center' });

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.text(noteType, MARGIN, 33);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 60);
  doc.text(`Client: ${clientName || '—'}`, MARGIN, 41);
  doc.text(`Date: ${date || '—'}`, MARGIN, 47);

  doc.setDrawColor(200, 200, 200);
  doc.line(MARGIN, 51, PAGE_W - MARGIN, 51);
  return 58;
}

// ── section heading ───────────────────────────────────────────────────────────
function sectionHead(doc, title, y) {
  if (y > 270) { doc.addPage(); y = 20; }
  doc.setFillColor(237, 240, 247);
  doc.rect(MARGIN, y - 4, CONTENT_W, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...BRAND_BLUE);
  doc.text(title.toUpperCase(), MARGIN + 2, y + 1);
  doc.setTextColor(0, 0, 0);
  return y + 9;
}

// ── field block ───────────────────────────────────────────────────────────────
function field(doc, label, value, y) {
  if (y > 272) { doc.addPage(); y = 20; }
  const text = value && String(value).trim() ? String(value).trim() : '—';

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(label, MARGIN, y);
  y += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  const lines = doc.splitTextToSize(text, CONTENT_W);
  lines.forEach(ln => {
    if (y > 278) { doc.addPage(); y = 20; }
    doc.text(ln, MARGIN, y);
    y += LINE_H;
  });
  return y + 3;
}

// ── page footers ──────────────────────────────────────────────────────────────
function addFooters(doc) {
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(160, 160, 160);
    doc.text(
      `Avenues Unlimited Counseling Center  •  Confidential  •  Page ${i} of ${total}`,
      PAGE_W / 2, 290, { align: 'center' }
    );
  }
}

// ── note-type formatters ──────────────────────────────────────────────────────
function fmtSOAP(doc, p, y) {
  y = sectionHead(doc, 'Subjective', y);
  y = field(doc, 'Mood', p.subjective?.mood, y);
  y = field(doc, 'Feelings Expressed', p.subjective?.feelingsExpressed, y);
  y = field(doc, 'Session Focus', p.subjective?.sessionFocus, y);

  y = sectionHead(doc, 'Objective', y);
  y = field(doc, 'Interventions Used', p.objective?.interventions, y);
  y = field(doc, 'Additional Techniques', p.objective?.additionalTechniques, y);
  y = field(doc, 'Engagement Level', p.objective?.engagementLevel, y);

  y = sectionHead(doc, 'Assessment', y);
  y = field(doc, 'Client Response', p.assessment?.clientResponse, y);
  y = field(doc, 'Client Progress', p.assessment?.clientProgress, y);

  y = sectionHead(doc, 'Plan', y);
  y = field(doc, 'Plan for Future Sessions', p.plan, y);

  if (p.sessionType) {
    y = sectionHead(doc, 'Session Info', y);
    y = field(doc, 'Session Type', p.sessionType, y);
  }
  return y;
}

const TA_QUESTIONS = [
  ['endVision',       'End Vision — How are you different at end of therapy?'],
  ['currentState',    'Currently'],
  ['desiredState',    'How you would like to be'],
  ['feelings',        'What I am feeling'],
  ['selfBeliefs',     'Beliefs about myself'],
  ['otherBeliefs',    'Beliefs about others'],
  ['actions',         'What I am doing'],
  ['enhanceLife',     'What could you change to enhance your life?'],
  ['howToAccomplish', 'What would you need to do to accomplish that?'],
  ['benefit',         'What about that would be beneficial or pleasing to you?'],
  ['measureSuccess',  'How would we both know you\'ve gotten there?'],
  ['sabotage',        'How could you sabotage your success?'],
  ['avoidSabotage',   'What will you do instead to ensure success?'],
  ['loss',            'Loss'],
  ['gain',            'Gain'],
];

function fmtTA(doc, p, y) {
  y = sectionHead(doc, 'TA Contract Responses', y);
  TA_QUESTIONS.forEach(([key, label]) => {
    if (p[key]) y = field(doc, label, p[key], y);
  });
  return y;
}

const INTAKE_FIELDS = [
  ['presentingProblems',              'Presenting Problems'],
  ['familyMedicalHistory',            'Family Medical History'],
  ['traumaHistory',                   'Trauma History'],
  ['traumaHistoryOther',              'Trauma History (Other)'],
  ['substanceAbuseStatus',            'Substance Abuse Status'],
  ['substanceAbuseFamilyHistory',     'Substance Abuse Family History'],
  ['substanceAbuseFamilyHistoryOther','Substance Abuse Family History (Other)'],
  ['previousTherapyExperiences',      'Previous Therapy Experiences'],
  ['previousTherapyNotes',            'Previous Therapy Notes'],
  ['selfHarmHistory',                 'Self-Harm History'],
  ['relationshipStatus',              'Relationship Status'],
  ['relationshipSatisfaction',        'Relationship Satisfaction'],
  ['protectiveFactors',               'Protective Factors / Strengths'],
  ['copingSkills',                    'Coping Skills'],
  ['mentalStatusExam',                'Mental Status Exam'],
];

function fmtIntake(doc, p, y) {
  INTAKE_FIELDS.forEach(([key, label]) => {
    const val = Array.isArray(p[key]) ? p[key].join(', ') : p[key];
    if (val) y = field(doc, label, val, y);
  });
  return y;
}

function fmtCollateral(doc, p, y) {
  y = sectionHead(doc, 'Contact Details', y);
  y = field(doc, 'Contact Type', p.contactType, y);
  y = field(doc, 'Contact With', p.contactWith, y);
  y = field(doc, 'Duration', p.contactDuration, y);
  y = field(doc, 'Permission to Contact', p.permissionToContact ? 'Yes' : 'No', y);

  y = sectionHead(doc, 'Content', y);
  y = field(doc, 'Purpose', p.contactPurpose, y);
  y = field(doc, 'Summary', p.contactSummary, y);
  y = field(doc, 'Follow-Up', p.followUp, y);
  return y;
}

// ── school note letter ────────────────────────────────────────────────────────
// School notes go out as the same letterhead letter the Preview / Print window
// shows in SchoolNoteComponents.jsx (not the generic field dump) — keep the
// wording and layout here in sync with that preview.
const LETTER_W = 215.9;
const LETTER_H = 279.4;
const LETTER_MARGIN = 22;
const LETTER_CONTENT_W = LETTER_W - LETTER_MARGIN * 2;
const LETTER_LINE_H = 5.6;
const LETTER_BOTTOM = LETTER_H - 28; // keep text clear of the footer image

// YYYY-MM-DD (or ISO timestamp) → MM/DD/YYYY, matching the preview's formatDate
function fmtLetterDate(dateStr) {
  if (!dateStr) return '';
  const parts = String(dateStr).split('T')[0].split('-');
  return parts.length === 3 ? `${parts[1]}/${parts[2]}/${parts[0]}` : String(dateStr);
}

function letterDayOfWeek(dateStr) {
  const parts = String(dateStr || '').split('T')[0].split('-').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return '';
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[new Date(parts[0], parts[1] - 1, parts[2]).getDay()];
}

async function loadPublicImage(path) {
  const res = await fetch(`${process.env.PUBLIC_URL || ''}${path}`);
  if (!res.ok) throw new Error(`Could not load ${path}`);
  const blob = await res.blob();
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  const size = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = reject;
    img.src = dataUrl;
  });
  return { dataUrl, ...size };
}

// Loads both branding images used by every letterhead document.
async function loadLetterheadImages() {
  const [letterhead, footer] = await Promise.all([
    loadPublicImage('/letterhead.png'),
    loadPublicImage('/footer.png'),
  ]);
  return { letterhead, footer };
}

// Letterhead, centered at the top of the first page. Returns the y to start writing at.
function placeLetterhead(doc, letterhead, headW = 72) {
  const headH = headW * (letterhead.h / letterhead.w);
  doc.addImage(letterhead.dataUrl, 'PNG', (LETTER_W - headW) / 2, 10, headW, headH);
  return 10 + headH + 12;
}

// Footer strip at the bottom of every page.
function stampLetterFooter(doc, footer) {
  const footW = LETTER_CONTENT_W;
  const footH = footW * (footer.h / footer.w);
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.addImage(footer.dataUrl, 'PNG', LETTER_MARGIN, LETTER_H - 12 - footH, footW, footH);
  }
}

// Cursor-based paragraph writer for letter-format documents. `w.y` is the
// current baseline; `w.lineH` can be changed when the font size changes.
function letterWriter(doc, startY, lineH = LETTER_LINE_H) {
  const w = {
    y: startY,
    lineH,
    // Start a new page if fewer than `needed` mm remain above the footer.
    ensure(needed) {
      if (w.y + needed > LETTER_BOTTOM) { doc.addPage(); w.y = 24; }
    },
    // Wrapped paragraph; blank lines in `text` become vertical gaps.
    para(text, gapAfter = 5, x = LETTER_MARGIN, width = LETTER_CONTENT_W) {
      String(text).split('\n').forEach((block) => {
        if (!block.trim()) { w.y += w.lineH; return; }
        doc.splitTextToSize(block, width).forEach((ln) => {
          if (w.y > LETTER_BOTTOM) { doc.addPage(); w.y = 24; }
          doc.text(ln, x, w.y);
          w.y += w.lineH;
        });
      });
      w.y += gapAfter;
    },
    // Hanging-indent line: `marker` at x, wrapped text `indent` mm to its right.
    marked(marker, text, gapAfter = 1, indent = 7, x = LETTER_MARGIN) {
      if (w.y > LETTER_BOTTOM) { doc.addPage(); w.y = 24; }
      doc.text(marker, x, w.y);
      w.para(text, gapAfter, x + indent, LETTER_CONTENT_W - (x - LETTER_MARGIN) - indent);
    },
  };
  return w;
}

async function buildSchoolNoteLetter(p, clientName) {
  const { letterhead, footer } = await loadLetterheadImages();

  const doc = new jsPDF({ format: 'letter' });
  const name = p.clientName || clientName || '';

  const w = letterWriter(doc, placeLetterhead(doc, letterhead));

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);

  const para = (text, gapAfter = 5) => w.para(text, gapAfter);

  para(fmtLetterDate(p.date), 6);
  para(`Re: ${name}`, 1);
  para(`DOB: ${fmtLetterDate(p.clientDob)}`, 8);
  para('To whom it may concern,');

  const dayOfWeek = p.appointmentDayOfWeek || letterDayOfWeek(p.appointmentDate);
  const content = p.customContent
    ? (p.customNoteContent || '')
    : `This letter is to document ${name}'s presence at Avenues Unlimited Counseling Center for ${p.appointmentTime ? `their ${p.appointmentTime}` : 'their'} session on ${dayOfWeek} ${fmtLetterDate(p.appointmentDate)}.${p.additionalNotes ? `\n\n${p.additionalNotes}` : ''}`;
  para(content);

  para('If further information is needed, please contact me directly using the information below.', 8);

  // Keep the closing + signature block together on one page
  w.ensure(55);
  para('Sincerely,', 3);

  if (p.signatureData && String(p.signatureData).startsWith('data:image')) {
    try {
      const sigW = 53;
      const props = doc.getImageProperties(p.signatureData);
      const sigH = Math.min(24, sigW * (props.height / props.width));
      doc.addImage(p.signatureData, 'PNG', LETTER_MARGIN, w.y, sigW, sigH);
      w.y += sigH + 6;
    } catch (e) { /* skip bad image */ }
  }

  para(`${p.therapistName || ''}${p.therapistCredentials ? `, ${p.therapistCredentials}` : ''}`, 1);
  para(p.therapistTitle || 'Therapist', 2);
  doc.setFontSize(10);
  para(`p. ${p.therapistPhone || ''}${p.therapistPhoneExtension ? ` ext. ${p.therapistPhoneExtension}` : ''}`, 0);
  para(`e. ${p.therapistEmail || ''}`, 0);

  stampLetterFooter(doc, footer);
  return doc;
}

// ── school consent (on-site school services) ──────────────────────────────────
// Letterhead US-Letter reproduction of the paper form: full section text with
// [X]/[ ] boxes and the three signature images. Wording comes from
// data/schoolConsentText.js so it matches the public page and the print view.
async function buildSchoolConsentDoc(submission, clientName) {
  let d = submission.data;
  if (typeof d === 'string') { try { d = JSON.parse(d); } catch { d = {}; } }
  d = d || {};
  const school = submission.school_name || d.schoolName || DEFAULT_SCHOOL_NAME;
  const t = withSchool(school);
  const yes = d.releaseAuthorized === true;
  const no = d.releaseAuthorized === false;

  const { letterhead, footer } = await loadLetterheadImages();
  const doc = new jsPDF({ format: 'letter' });
  const w = letterWriter(doc, placeLetterhead(doc, letterhead, 60), 4.8);

  const BODY = 9.5;
  const body = () => { doc.setFont('helvetica', 'normal'); doc.setFontSize(BODY); doc.setTextColor(0, 0, 0); };
  const bold = () => { doc.setFont('helvetica', 'bold'); doc.setFontSize(BODY); doc.setTextColor(0, 0, 0); };

  const heading = (title) => {
    w.ensure(18);
    w.y += 2;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10.5); doc.setTextColor(...BRAND_BLUE);
    w.para(title, 1.5);
    body();
  };
  // ASCII markers only: jsPDF's built-in Helvetica mangles "•" and "–".
  const bullet = (text) => { body(); w.marked('-', text, 0.8, 5); };
  const check = (on, text, x = LETTER_MARGIN) => { body(); w.marked(on ? '[X]' : '[  ]', text, 0.8, 9, x); };
  const labelled = (label, value, x = LETTER_MARGIN) => {
    w.ensure(8);
    bold(); doc.text(`${label}:`, x, w.y);
    const lw = doc.getTextWidth(`${label}:`) + 2;
    body();
    w.para(value && String(value).trim() ? String(value).trim() : '-', 1, x + lw, LETTER_CONTENT_W - (x - LETTER_MARGIN) - lw);
  };
  const signature = (label, dataUrl, printedName, dateStr) => {
    // Keep label, image and its lines together on one page
    w.ensure(42);
    bold(); doc.text(`${label}:`, LETTER_MARGIN, w.y); w.y += 2;
    let drewImage = false;
    if (dataUrl && String(dataUrl).startsWith('data:image')) {
      try {
        const sigW = 60;
        const props = doc.getImageProperties(dataUrl);
        const sigH = Math.min(20, sigW * (props.height / props.width));
        doc.addImage(dataUrl, 'PNG', LETTER_MARGIN + 2, w.y, sigW, sigH);
        w.y += sigH + 1;
        drewImage = true;
      } catch (e) { /* skip bad image */ }
    }
    if (!drewImage) { w.y += 10; }
    doc.setDrawColor(0, 0, 0);
    doc.line(LETTER_MARGIN + 2, w.y, LETTER_MARGIN + 2 + 75, w.y);
    w.y += 5;
    if (printedName !== undefined) labelled('Printed Name', printedName);
    labelled('Date', fmtLetterDate(dateStr));
    w.y += 2;
  };

  // Title
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12.5); doc.setTextColor(0, 0, 0);
  doc.splitTextToSize(SCHOOL_CONSENT_TITLE, LETTER_CONTENT_W).forEach((ln) => {
    doc.text(ln, LETTER_W / 2, w.y, { align: 'center' });
    w.y += 6;
  });
  w.y += 3;

  // Header fields
  labelled(HEADER_LABELS.clientName, d.clientName || clientName);
  labelled(HEADER_LABELS.clientDob, fmtLetterDate(d.clientDob));
  labelled(HEADER_LABELS.schoolName, school);
  labelled(HEADER_LABELS.gradeTeacher, d.gradeTeacher);

  // 1
  heading(t.purpose.title);
  w.para(t.purpose.body, 2);

  // 2
  heading(t.services.title);
  t.services.bullets.forEach(b => bullet(bulletLine(b)));
  w.y += 1;

  // 3
  heading(t.confidentiality.title);
  w.para(t.confidentiality.intro, 1);
  t.confidentiality.bullets.forEach(bullet);
  w.y += 1;

  // 4
  heading(t.consent.title);
  w.para(t.consent.intro, 1);
  t.consent.bullets.forEach(bullet);
  w.y += 2;

  // 5
  heading(t.release.title);
  doc.setFont('helvetica', 'italic'); doc.setFontSize(BODY);
  w.para(t.release.checkOne, 1);
  check(yes, `${t.release.yesLead} - ${t.release.yesText}`);
  const IN = LETTER_MARGIN + 9; // indented under the "Yes" text
  w.y += 1;
  labelled(t.release.personnelLabel, yes ? d.releasePersonnel : '', IN);
  bold(); w.ensure(6); doc.text(t.release.sharedHeading, IN, w.y); w.y += w.lineH;
  t.release.shareOptions.forEach(o => check(yes && !!d[o.key], o.label, IN));
  check(yes && !!d.shareOther, `${t.release.otherLabel}: ${yes && d.shareOther ? (d.shareOtherSpecify || '') : ''}`, IN);
  w.y += 1;
  body();
  w.para(
    `${t.release.expirationLead} ${yes && d.releaseExpiration ? d.releaseExpiration : '____________'} ${t.release.expirationHint}. ${t.release.expirationTail}`,
    2, IN, LETTER_CONTENT_W - (IN - LETTER_MARGIN)
  );
  check(no, `${t.release.noLead} - ${t.release.noText}`);
  w.y += 2;

  // 6
  heading(t.acknowledgment.title);
  w.para(t.acknowledgment.body, 2);
  signature(t.acknowledgment.signatureLabel, d.ackSignatureData, d.ackPrintedName, d.ackSignatureDate);

  if (submission.created_at) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(110, 110, 110);
    w.ensure(6);
    doc.text(`Submitted electronically ${new Date(submission.created_at).toLocaleString()}`, LETTER_MARGIN, w.y);
  }

  stampLetterFooter(doc, footer);
  return doc;
}

export async function generateSchoolConsentPDF(submission, clientName) {
  const date = submission.created_at
    ? new Date(submission.created_at).toLocaleDateString()
    : new Date().toLocaleDateString();
  const doc = await buildSchoolConsentDoc(submission, clientName);
  const safeName = (clientName || 'Client').replace(/[,\s]+/g, '_');
  return {
    pdfBase64: doc.output('datauristring').split(',')[1],
    fileName:  `School_Consent_${safeName}_${date.replace(/\//g, '-')}.pdf`,
    noteType:  'School Consent',
    date,
  };
}

function fmtHandwritten(doc, p, y) {
  if (p.title) y = field(doc, 'Title', p.title, y);
  if (p.drawingData) {
    try {
      if (y > 200) { doc.addPage(); y = 20; }
      const imgH = Math.min(120, 270 - y);
      doc.addImage(p.drawingData, 'PNG', MARGIN, y, CONTENT_W, imgH);
      y += imgH + 6;
    } catch (_) {
      y = field(doc, 'Note', 'Handwritten image could not be rendered.', y);
    }
  }
  return y;
}

// ── ROI formatter ─────────────────────────────────────────────────────────────
function fmtROI(doc, s, y) {
  const gs = (camel, snake) => s[camel] || s[snake] || '';
  const gv = (camel, snake) => {
    const v = s[camel] !== undefined && s[camel] !== null ? s[camel] : s[snake];
    return v === true || v === 'true' || v === 1 || v === '1' || v === 't';
  };

  // Single-line checkbox row: "[X] label" / "[ ] label" (ASCII — jsPDF helvetica
  // can't render Unicode checkmarks reliably).
  const checkLine = (checked, label, yy) => {
    if (yy > 274) { doc.addPage(); yy = 20; }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    const lines = doc.splitTextToSize(`${checked ? '[X]' : '[  ]'} ${label}`, CONTENT_W);
    lines.forEach((ln, i) => {
      if (yy > 280) { doc.addPage(); yy = 20; }
      doc.text(ln, MARGIN + (i === 0 ? 0 : 6), yy);
      yy += LINE_H;
    });
    return yy + 1;
  };

  const sigImage = (label, dataUrl, yy) => {
    if (!dataUrl || !String(dataUrl).startsWith('data:image')) return yy;
    if (yy > 235) { doc.addPage(); yy = 20; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(100, 100, 100);
    doc.text(label, MARGIN, yy); yy += 4;
    try { doc.addImage(dataUrl, 'PNG', MARGIN, yy, 70, 26); } catch (e) { /* skip bad image */ }
    return yy + 30;
  };

  y = sectionHead(doc, 'Client Information', y);
  y = field(doc, 'Client Name', gs('clientName', 'client_name'), y);
  y = field(doc, 'Date of Birth', gs('clientDob', 'client_dob'), y);
  y = field(doc, 'Address', gs('clientAddress', 'client_address'), y);
  y = field(doc, 'Phone', gs('clientPhone', 'client_phone'), y);

  y = sectionHead(doc, 'Release Information To', y);
  y = field(doc, 'Name', gs('authorizeReleaseTo', 'authorize_release_to'), y);
  y = field(doc, 'Relationship', gs('releaseToRelationship', 'release_to_relationship'), y);
  y = field(doc, 'Phone / Email', gs('releaseToPhone', 'release_to_phone'), y);

  y = sectionHead(doc, 'Authorized Information Release', y);
  const release = [
    ['shareProgressWithProvider', 'share_progress_with_provider', 'Share progress and treatment details with my primary care provider or psychiatrist'],
    ['coordinateCareWithPsychiatrist', 'coordinate_care_with_psychiatrist', 'Coordinate care with my psychiatrist or prescribing provider'],
    ['shareWithSchool', 'share_with_school', "Share treatment information with my child's school"],
    ['speakWithFamily', 'speak_with_family', 'Speak with a family member, partner, or support person'],
    ['billUsingOtherCard', 'bill_using_other_card', 'Bill services using a card not in my name'],
    ['provideDocumentationToProvider', 'provide_documentation_to_provider', 'Provide documentation to another mental health provider'],
    ['allowEmergencyContactInform', 'allow_emergency_contact_inform', 'Allow emergency contacts to be informed in case of a crisis'],
    ['schedulingInfoOnly', 'scheduling_info_only', 'Scheduling or appointment information only'],
    ['treatmentProgress', 'treatment_progress', 'Treatment progress and participation'],
    ['diagnosisAndPlan', 'diagnosis_and_plan', 'Diagnosis and treatment plan'],
    ['billingAndPayment', 'billing_and_payment', 'Billing and payment details'],
    ['fullClinicalRecords', 'full_clinical_records', 'Full clinical records'],
  ];
  release.forEach(([c, sn, label]) => { y = checkLine(gv(c, sn), label, y); });
  const releaseOther = gs('releaseOtherSpecify', 'release_other_specify');
  if (releaseOther) y = checkLine(true, `Other: ${releaseOther}`, y);

  y = sectionHead(doc, 'Purpose of Disclosure', y);
  const purposes = [
    ['purposeCoordinationCare', 'purpose_coordination_care', 'Coordination of care'],
    ['purposeBilling', 'purpose_billing', 'Billing'],
    ['purposeEmergency', 'purpose_emergency', 'Emergency or safety planning'],
    ['purposeSupportFromFamily', 'purpose_support_from_family', 'Support from family/friends'],
  ];
  purposes.forEach(([c, sn, label]) => { y = checkLine(gv(c, sn), label, y); });
  const purposeOther = gs('purposeOtherSpecify', 'purpose_other_specify');
  if (purposeOther) y = checkLine(true, `Other: ${purposeOther}`, y);

  y = sectionHead(doc, 'Duration', y);
  let durationText = '—';
  if (gv('durationOneYear', 'duration_one_year')) durationText = 'One year from today';
  else if (gv('durationEndOfTreatment', 'duration_end_of_treatment')) durationText = 'End of treatment';
  else if (gv('durationSpecificDate', 'duration_specific_date')) durationText = `Until: ${gs('specificExpirationDate', 'specific_expiration_date')}`;
  y = field(doc, 'Duration', durationText, y);

  if (gv('eapAuthorizeSharing', 'eap_authorize_sharing') || gs('eapEmployer', 'eap_employer')) {
    y = sectionHead(doc, 'Employee Assistance Program (EAP)', y);
    const eapEmployer = gs('eapEmployer', 'eap_employer');
    if (eapEmployer) y = field(doc, 'Employer', eapEmployer, y);
    y = checkLine(gv('eapAuthorizeSharing', 'eap_authorize_sharing'), 'Authorized to share session dates and billing information with employer', y);
    y = checkLine(gv('eapUnderstandNoDetails', 'eap_understand_no_details'), 'Understands no clinical details will be shared unless explicitly authorized', y);
    y = checkLine(gv('eapUnderstandRefusalImpact', 'eap_understand_refusal_impact'), 'Understands refusal may impact ability to use EAP services', y);
  }

  y = sectionHead(doc, 'Authorization Details', y);
  y = field(doc, 'Submitted', s.created_at ? new Date(s.created_at).toLocaleString() : '', y);
  y = field(doc, 'Client Signed', gs('clientSignatureDate', 'client_signature_date'), y);
  const guardianName = gs('guardianName', 'guardian_name');
  if (guardianName) {
    y = field(doc, 'Guardian', guardianName, y);
    y = field(doc, 'Guardian Signed', gs('guardianSignatureDate', 'guardian_signature_date'), y);
  }
  const witnessName = gs('witnessName', 'witness_name');
  if (witnessName) {
    y = field(doc, 'Witness', witnessName, y);
    y = field(doc, 'Witness Signed', gs('witnessSignatureDate', 'witness_signature_date'), y);
  }

  y = sectionHead(doc, 'Signatures', y);
  y = sigImage('Client Signature', gs('clientSignatureData', 'client_signature_data'), y);
  y = sigImage('Guardian Signature', gs('guardianSignatureData', 'guardian_signature_data'), y);
  y = sigImage('Witness Signature', gs('witnessSignatureData', 'witness_signature_data'), y);

  return y;
}

// ── public API ────────────────────────────────────────────────────────────────
// Async because the school note letter loads the letterhead/footer images.
export async function generateNotePDF(note, clientName) {
  let p = {};
  try { p = typeof note.notes === 'string' ? JSON.parse(note.notes) : (note.notes || {}); } catch {}

  const noteType = p.type || 'SOAP Note';
  const date = p.date || new Date(note.created_at).toLocaleDateString();

  if (p.type === 'SchoolNote') {
    const letter = await buildSchoolNoteLetter(p, clientName);
    const safeLetterName = (clientName || 'Client').replace(/[,\s]+/g, '_');
    return {
      pdfBase64: letter.output('datauristring').split(',')[1],
      fileName:  `School_Note_${safeLetterName}_${date.replace(/\//g, '-')}.pdf`,
      noteType:  'School Note',
      date,
    };
  }

  const doc = new jsPDF();
  let y = addHeader(doc, noteType, clientName, date);

  if      (!p.type || p.type === 'SOAP Note')        y = fmtSOAP(doc, p, y);
  else if (p.type === 'TA Contract')                 y = fmtTA(doc, p, y);
  else if (p.type === 'IntakeAssessment')            y = fmtIntake(doc, p, y);
  else if (p.type === 'CollateralContact')           y = fmtCollateral(doc, p, y);
  else if (p.type === 'HandwrittenNote')             y = fmtHandwritten(doc, p, y);

  addFooters(doc);

  const safeDate  = date.replace(/\//g, '-');
  const safeName  = (clientName || 'Client').replace(/[,\s]+/g, '_');
  const safeType  = noteType.replace(/\s+/g, '_');
  return {
    pdfBase64: doc.output('datauristring').split(',')[1],
    fileName:  `${safeType}_${safeName}_${safeDate}.pdf`,
    noteType,
    date,
  };
}

export function generateROIPDF(submission, clientName) {
  const doc = new jsPDF();
  const date = submission.created_at
    ? new Date(submission.created_at).toLocaleDateString()
    : new Date().toLocaleDateString();

  let y = addHeader(doc, 'Release of Information', clientName, date);
  y = fmtROI(doc, submission, y);
  addFooters(doc);

  const safeName = (clientName || 'Client').replace(/[,\s]+/g, '_');
  return {
    pdfBase64: doc.output('datauristring').split(',')[1],
    fileName:  `ROI_${safeName}_${date.replace(/\//g, '-')}.pdf`,
    noteType:  'Release of Information',
    date,
  };
}
